import { DESCRIPTION_LIMIT, HEADLINE_LIMIT } from '../validateGoogleContent.js';

// FRANCHISE CAMPAIGN COPY — THE FIVE OUTPUTS, AND THE SIX LENSES.
//
// ⚠️⚠️ THE GOOGLE LIMITS ARE THE EXISTING CONSTANTS, IMPORTED, NEVER RESTATED.
// `HEADLINE_LIMIT` (30) and `DESCRIPTION_LIMIT` (90) are what the grid's "Check
// Errors" button and the CSV export gate already enforce. A second definition of
// "valid" here is how a generator starts approving copy the export then rejects
// — the AM would insert it, the campaign would look finished, and it would fail
// at the last step in front of a deadline.
//
// ⚠️ META HAS NO PLATFORM CHARACTER LIMIT IN THIS SCHEMA. The 3,000 / 255 figures
// are the AUTHORED sanity caps in server/modules/campaigns/schemas.ts, not
// Facebook limits, and the Ad Builder's soft caps in metaAdDraft.ts are a
// DIFFERENT feature with a different contract — do not import them here. The
// workspace may show an advisory count; nothing may FAIL on it.

export type CampaignCopyPlatform = 'meta' | 'google';

export type CampaignCopyFieldId =
  | 'meta_body'
  | 'meta_headline'
  | 'meta_description'
  | 'google_headline'
  | 'google_description';

export interface CampaignCopyField {
  id: CampaignCopyFieldId;
  platform: CampaignCopyPlatform;
  label: string;
  /** Plural, for "3 of 15 headlines". */
  pluralLabel: string;
  /**
   * The AUTHORED cap from the campaign zod schema — what a save would reject.
   * Counts the string as typed, tokens and all.
   */
  authoredMaxChars: number;
  /**
   * The PLATFORM cap, applied to the RESOLVED string per location. `null` for
   * Meta, which has none here — and null must mean "no check", never 0.
   */
  platformMaxChars: number | null;
  /** How many entries the campaign array holds in total. */
  maxEntries: number;
  /**
   * How many options the AM inserts. Google headlines are the odd one out: they
   * fill toward the 15 rather than taking a fixed number.
   */
  inserts: 'one' | 'fill';
  /** What this field is for, in the prompt. */
  purpose: string;
}

export const CAMPAIGN_COPY_FIELDS: readonly CampaignCopyField[] = [
  {
    id: 'meta_body',
    platform: 'meta',
    label: 'Meta body copy',
    pluralLabel: 'body copy entries',
    authoredMaxChars: 3_000,
    platformMaxChars: null,
    maxEntries: 5,
    inserts: 'one',
    purpose:
      'The main text of a Facebook / Instagram ad — the paragraph above the creative. It carries the offer, the reason to act and the call to action. This is where {suburb} and {gymInfo} belong.',
  },
  {
    id: 'meta_headline',
    platform: 'meta',
    label: 'Meta headline',
    pluralLabel: 'headlines',
    authoredMaxChars: 255,
    platformMaxChars: null,
    maxEntries: 5,
    inserts: 'one',
    purpose:
      'The bold line under a Meta ad creative, next to the call-to-action button. Short and punchy — a few words, not a sentence. Merge tokens are rare here and are NOT required; do not force one in.',
  },
  {
    id: 'meta_description',
    platform: 'meta',
    label: 'Meta description',
    pluralLabel: 'descriptions',
    authoredMaxChars: 255,
    platformMaxChars: null,
    maxEntries: 3,
    inserts: 'one',
    purpose:
      'The smaller supporting line under a Meta headline. One clarifying detail — the qualifier, the deadline, the "what you get" — never a repeat of the headline.',
  },
  {
    id: 'google_headline',
    platform: 'google',
    label: 'Google headline',
    pluralLabel: 'headlines',
    // The zod cap on an authored Google headline is deliberately generous (200)
    // because the authored string carries tokens that resolve SHORTER. The real
    // constraint is the platform one below, applied to the resolved value.
    authoredMaxChars: 200,
    platformMaxChars: HEADLINE_LIMIT,
    maxEntries: 15,
    inserts: 'fill',
    purpose:
      'One of up to 15 interchangeable Google responsive-search-ad headlines. Google mixes and matches them, so each must stand alone and make sense beside any other. {shortSuburb} is the normal way to make one local.',
  },
  {
    id: 'google_description',
    platform: 'google',
    label: 'Google description',
    pluralLabel: 'descriptions',
    authoredMaxChars: 1_000,
    platformMaxChars: DESCRIPTION_LIMIT,
    maxEntries: 4,
    inserts: 'fill',
    purpose:
      'One of up to 4 Google responsive-search-ad description lines. A full sentence carrying the offer or the benefit, ending in a reason to click.',
  },
] as const;

export function campaignCopyField(id: CampaignCopyFieldId): CampaignCopyField {
  const found = CAMPAIGN_COPY_FIELDS.find((f) => f.id === id);
  // Every id is in the union, so this is unreachable through the type system —
  // it exists so a hand-rolled request gets a clear answer rather than undefined
  // flowing into a length check.
  if (!found) throw new Error(`Unknown campaign copy field: ${id}`);
  return found;
}

export function isCampaignCopyField(value: unknown): value is CampaignCopyFieldId {
  return CAMPAIGN_COPY_FIELDS.some((f) => f.id === value);
}

// ---------------------------------------------------------------------------
// The variation axis
// ---------------------------------------------------------------------------

/**
 * ⚠️⚠️ SIX DIRECT-RESPONSE LENSES, NOT SIX LENGTHS (Scott, 2026-08-26). Each is a
 * FULLY DEVELOPED option in that style, not a trimmed version of one idea — the
 * caption drafter learned this the hard way in round 3, where three "variations"
 * turned out to be three attempts at the same caption and the AM was choosing
 * between three similar mediocrities rather than between real approaches.
 *
 * The ids are the contract with the model (structured output) AND the labels the
 * workspace renders, so an AM is choosing an APPROACH rather than guessing why
 * two options differ. Short and long come from the lens plus the field's own cap,
 * never from a separate "length" knob.
 */
export const CAMPAIGN_COPY_LENSES = [
  {
    id: 'problem-agitate-solve',
    label: 'Problem → solution',
    blurb: 'Names the frustration, sharpens it, then offers the way out.',
    instruction:
      'PROBLEM-AGITATE-SOLVE. Open on the specific frustration this reader actually has — not a generic pain point, the real one. Make it concrete enough that they recognise themselves in it, then land the offer as the way out. The problem must be one the offer genuinely solves; do not invent a hardship to sell against.',
  },
  {
    id: 'offer-led',
    label: 'Offer-led',
    blurb: 'Leads with the deal itself, plainly, up front.',
    instruction:
      'OFFER-LED. Lead with the offer itself, in the first few words, with no build-up. State plainly what they get. Then the one qualifier that matters and the action. This is the most direct of the six — if the offer is strong, get out of its way.',
  },
  {
    id: 'social-proof',
    label: 'Social proof',
    blurb: 'Leans on who else is already there.',
    instruction:
      'SOCIAL PROOF. Lead on the fact that other people — locals, members, owners — are already doing this. ⚠️ You may ONLY use numbers, ratings, review counts or testimonials that appear in the brief or the reference material. If you have none, write the proof qualitatively and honestly ("join the members already training here") rather than inventing a figure. A made-up "join 2,000 members" is a fabricated claim, not a flourish.',
  },
  {
    id: 'urgency',
    label: 'Urgency',
    blurb: 'Built around the deadline or the limit.',
    instruction:
      'URGENCY / SCARCITY. Built around a real deadline or a real limit. ⚠️ THE DEADLINE MUST BE ONE YOU WERE GIVEN. If the brief or the reference material states an end date, a spot count or a limit, that is your engine. If it does NOT, do not manufacture one — no "limited spots", no "ends soon", no "while stocks last". Use the urgency that genuinely exists (the start of the month, the season) or leave a [square bracket] gap for the human to fill.',
  },
  {
    id: 'curiosity',
    label: 'Curiosity gap',
    blurb: 'Opens a question the click answers.',
    instruction:
      'CURIOSITY GAP. Open a specific question the reader wants answered and make the click the answer. ⚠️ The gap must be honest — the thing you hint at has to be genuinely on the other side. Vague teasing ("you won\'t believe what we\'re offering") is the failure mode of this lens: it is content-free, and it is exactly the hedging filler the rules forbid. Be specific about the SHAPE of what is coming without giving it away.',
  },
  {
    id: 'objection-handling',
    label: 'Objection handling',
    blurb: 'Answers the reason they would say no.',
    instruction:
      'OBJECTION HANDLING. Name the reason this reader would say no — the cost, the lock-in contract, the intimidation of walking in, the time — and answer it directly with something the offer actually does. ⚠️ Only answer it with a fact you were given. "No lock-in contract" is a claim about the client\'s terms; if the brief does not say so, do not say so.',
  },
] as const;

export type CampaignCopyLensId = (typeof CAMPAIGN_COPY_LENSES)[number]['id'];

export function isCampaignCopyLens(value: unknown): value is CampaignCopyLensId {
  return CAMPAIGN_COPY_LENSES.some((l) => l.id === value);
}

export function campaignCopyLensLabel(id: string): string {
  return CAMPAIGN_COPY_LENSES.find((l) => l.id === id)?.label ?? 'Option';
}

export function campaignCopyLensBlurb(id: string): string {
  return CAMPAIGN_COPY_LENSES.find((l) => l.id === id)?.blurb ?? '';
}
